import React, {useState} from 'react';
import {TableRow, TableCell, Table, TableHead, Button, TableBody, Typography, Box} from '@mui/material';
import {ArrowRightAlt, Edit, ErrorOutline, ErrorSharp, CheckCircle, CheckCircleOutline, Replay} from '@mui/icons-material';
import FileUploadBox from '../../../ManageSamples/FileUploadBox';
import {GradientCircularProgress} from '../../../../components/Icons/GradientCircularProgress';
import EditMetaForm from '../../../ManageSamples/EditMetaForm';
import Popup from '../../../../components/popup';
import {getAppConfig} from '../../../../utils/config';
import {useAuth0} from '@auth0/auth0-react';
import {uploadFileMultiPart} from '../../../../utils/file-upload';
import {addFileToSample, fileToBffEntry} from '../../../../utils/helpers';

interface SampleFilesTableProps {
    sample: any;
    experiment: Readonly<any>;
    /** Expected filenames for this sample derived from the filenames CSV in step 3. */
    expectedFilenames?: string[];
    /** Called after the file(s) have been uploaded successfully. */
    onUploadSuccess?: () => void;
    /** Called after a file's metadata was edited. */
    onFileMetaEdited?: () => void;
}

/**
 * List a sample's files and enable the user to upload new files. A file's
 * metadata can also be edited.
 *
 * File upload in this component happens in two situations:
 * - For a failed upload, the user is asked to select the specific file that
 *   failed
 * - When files are missing, the user can select files to upload. Metadata is
 *   derived from the filename
 */
export const SampleFilesTable = ({
    sample,
    experiment,
    expectedFilenames = [],
    onUploadSuccess,
    onFileMetaEdited
}: SampleFilesTableProps) => {
    const {getAccessTokenSilently} = useAuth0();
    const {apiHost} = getAppConfig();

    /** Sets the file (as returned by bff) that needs its metadata edited. */
    const [editMeta, setEditMeta] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    /** Sets the file that failed to upload. User is then expected to select it from their FS. */
    const [retryUpload, setRetryUpload] = useState<any>(null);
    const [uploading, setUploading] = useState(false);
    const [finished, setFinished] = useState<string[]>([]);

    // identifies missing files for user to upload
    // expectedFilenames contains expected files, sample.files contains current
    const missingExpectedFilenames = expectedFilenames.filter(
        name => !(sample?.files || []).some((existingFile: any) => existingFile?.name === name)
    );
    const hasMissingExpectedFilenames = missingExpectedFilenames.length > 0;
    const selectedNames = selectedFiles.map(file => file.name);
    const uniqueSelectedNames = new Set(selectedNames);

    // files user selects that doesn't belong with sample
    const unexpectedSelectedNames =
        !retryUpload && hasMissingExpectedFilenames ? selectedNames.filter(n => !missingExpectedFilenames.includes(n)) : [];
    // files user needs to upload
    const missingRequiredSelectedNames =
        !retryUpload && hasMissingExpectedFilenames ? missingExpectedFilenames.filter(n => !uniqueSelectedNames.has(n)) : [];
    // if user tries to upload a file sample alr has
    const duplicateSelectedNames = !retryUpload ? selectedNames.filter((name, index) => selectedNames.indexOf(name) !== index) : [];
    // bool that catches any issues with sample files
    const hasFilenameMismatch =
        unexpectedSelectedNames.length > 0 || missingRequiredSelectedNames.length > 0 || duplicateSelectedNames.length > 0;

    /**
     * Uploads all files in the selectedFiles array. If no previous file
     * existed, a new RawFile record is created in the database.
     */
    async function processAllFiles() {
        if (!retryUpload && hasFilenameMismatch) {
            return;
        }
        setUploading(true);
        const apiAccessToken = await getAccessTokenSilently();
        let filesToUpload: {id: string; name: string; fileObj: File}[] = [];
        if (!!retryUpload) {
            // When an upload has failed, the user is asked to select the file
            // with the same name as the failed upload. Only this file is
            // uploaded. Hence selectedFiles[0]
            filesToUpload = [{id: retryUpload.id, name: retryUpload.name, fileObj: selectedFiles[0]}];
        } else {
            // User is adding files to a sample for the first time
            filesToUpload = await Promise.all(
                selectedFiles.map(async (file: File) => {
                    const preppedFile = fileToBffEntry(
                        file,
                        experiment.sequencing_sense || '',
                        experiment.sequencing_read_type && experiment.sequencing_read_type.toLowerCase(),
                        sample.id
                    );
                    const fileRecord = await addFileToSample(apiHost, apiAccessToken, experiment.id, sample.id, preppedFile);
                    return {id: fileRecord.id, name: fileRecord.name, fileObj: file};
                })
            );
        }
        for (const fileToUpload of filesToUpload) {
            // TODO: Just soft ignores error.
            await uploadFileMultiPart(apiHost, apiAccessToken, experiment.id, sample.id, fileToUpload.id, fileToUpload.fileObj)
                .then(_data => {
                    setFinished((state: any) => [fileToUpload.name, ...state]);
                })
                .catch(err => {
                    console.error('Error uploading file:', err);
                });
        }
        setRetryUpload(null);
        setUploading(false);
        onUploadSuccess && onUploadSuccess();
    }

    return (
        <>
            <Table size="small" sx={{mt: '3px'}}>
                <TableHead>
                    <TableRow sx={{bgcolor: 'surface-container-high'}}>
                        <TableCell>File ID</TableCell>
                        <TableCell>File Name</TableCell>
                        <TableCell sx={{pl: 3}}>Status</TableCell>
                        <TableCell>Lane Number</TableCell>
                        <TableCell>Read Number</TableCell>
                        <TableCell> </TableCell>
                    </TableRow>
                </TableHead>

                {/* --- File Display for Sample  ---> */}
                <TableBody sx={{bgcolor: 'surface-container-low'}}>
                    {sample?.files?.map((fileEntry: any) => (
                        <TableRow key={fileEntry.id}>
                            <TableCell align="left">SM-{fileEntry.id.slice(0, 5)}</TableCell>
                            <TableCell>{fileEntry.name}</TableCell>
                            {/* ---- File upload Retry button & file upload popup ----> */}
                            <TableCell align="left">
                                {fileEntry.status === 'ready' ? (
                                    <Typography>
                                        <CheckCircleOutline color="disabled" sx={{fontSize: '18px'}} /> ready
                                    </Typography>
                                ) : (
                                    <Button
                                        variant="text"
                                        sx={{mx: 0, px: 1}}
                                        startIcon={<ErrorOutline color="error" sx={{fontSize: '18px', mx: 0}} />}
                                        endIcon={<Replay sx={{mx: 0}} />}
                                        color="primary"
                                        onClick={() => setRetryUpload(fileEntry)}>
                                        {fileEntry.status}
                                    </Button>
                                )}
                            </TableCell>
                            {/* ---- Files with no Metadata Display add Metadata warning and button Here ----> */}
                            <TableCell align="center">
                                {fileEntry?.lane_number ? (
                                    '00' + fileEntry?.lane_number
                                ) : (
                                    <Button
                                        variant="text"
                                        startIcon={<ErrorOutline />}
                                        endIcon={<ArrowRightAlt />}
                                        color="error"
                                        onClick={e => setEditMeta(fileEntry)}>
                                        <Typography variant="label" size="medium - prominent">
                                            Add Metadata
                                        </Typography>
                                    </Button>
                                )}
                            </TableCell>
                            <TableCell align="center">
                                {fileEntry?.read_number || (
                                    <Button
                                        variant="text"
                                        startIcon={<ErrorOutline />}
                                        endIcon={<ArrowRightAlt />}
                                        color="error"
                                        onClick={e => setEditMeta(fileEntry)}>
                                        <Typography variant="label" size="medium - prominent">
                                            Add Metadata
                                        </Typography>
                                    </Button>
                                )}
                            </TableCell>
                            <TableCell>
                                <Edit
                                    data-testid="EditIcon"
                                    sx={{fontSize: '18px', cursor: 'pointer'}}
                                    onClick={e => setEditMeta(fileEntry)}
                                />
                            </TableCell>
                        </TableRow>
                    ))}

                    {/* --- File Upload Box Conditional Rendering For File Uploading ---> */}
                    <TableRow sx={{py: 0}}>
                        <TableCell colSpan={8}>
                            <Box p={1} display={'flex'} flexDirection={'column'} justifyContent={'center'}>
                                {sample?.files?.length < experiment?.files_per_sample &&
                                    (!uploading ? (
                                        <>
                                            <FileUploadBox
                                                maxFiles={experiment.files_per_sample - sample.files.length}
                                                selectedFiles={selectedFiles}
                                                onFilesSelected={setSelectedFiles}
                                            />
                                            {!retryUpload && hasMissingExpectedFilenames && (
                                                <Typography variant="body" size="small" sx={{mt: 1, mx: 1}}>
                                                    Expected filename{missingExpectedFilenames.length > 1 ? 's' : ''}:
                                                    {missingExpectedFilenames.join(', ')}
                                                </Typography>
                                            )}
                                            {hasFilenameMismatch && (
                                                <Typography variant="title" size="small" color="error" px={2} mt={1}>
                                                    <ErrorSharp fontSize="small" /> Selected files must match expected filenames:
                                                    {missingRequiredSelectedNames.length > 0 && (
                                                        <Typography variant="body" size="small" color="error">
                                                            Missing: {missingRequiredSelectedNames.join(', ')}
                                                        </Typography>
                                                    )}
                                                    {unexpectedSelectedNames.length > 0 && (
                                                        <Typography variant="body" size="small" color="error">
                                                            Unexpected: {unexpectedSelectedNames.join(', ')}
                                                        </Typography>
                                                    )}
                                                    {duplicateSelectedNames.length > 0 && (
                                                        <Typography variant="body" size="small" color="error">
                                                            Duplicates: {Array.from(new Set(duplicateSelectedNames)).join(', ')}
                                                        </Typography>
                                                    )}
                                                </Typography>
                                            )}
                                            <Button
                                                variant="contained"
                                                size="small"
                                                disabled={
                                                    selectedFiles?.length + sample.files.length < experiment.files_per_sample ||
                                                    hasFilenameMismatch
                                                }
                                                sx={{mt: 1, alignSelf: 'center', px: 2, py: 1}}
                                                onClick={processAllFiles}>
                                                Upload Files
                                            </Button>
                                        </>
                                    ) : (
                                        <Box>
                                            {selectedFiles?.map((f: any) => (
                                                <Typography variant="body" size="medium">
                                                    {f.name}{' '}
                                                    {finished.includes(f.name) ? (
                                                        <CheckCircle />
                                                    ) : (
                                                        <GradientCircularProgress gradientId="gradient-add-files" />
                                                    )}{' '}
                                                </Typography>
                                            ))}
                                        </Box>
                                    ))}
                                {sample?.files?.length == experiment?.files_per_sample && (
                                    <Typography
                                        align="center"
                                        border={'1px dashed'}
                                        borderColor={'outline-variant'}
                                        p={1}
                                        sx={{fontSize: '90%'}}
                                        color={'outline-variant'}>
                                        You've reached the maximum number of files allowed for this sample.
                                    </Typography>
                                )}
                            </Box>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>

            {/* Popup when needing to select a file that failed to upload */}
            <Popup isOpen={!!retryUpload}>
                <Box p={3}>
                    {!uploading ? (
                        <Box>
                            <Typography variant="title" size="large" p={1} mb={2}>
                                Select the File: {retryUpload?.name}
                            </Typography>
                            <FileUploadBox maxFiles={1} selectedFiles={selectedFiles} onFilesSelected={setSelectedFiles} />
                            {selectedFiles?.[0] && selectedFiles[0]?.name !== retryUpload?.name && (
                                <Typography variant="title" size="medium" color="error" px={2} mt={1}>
                                    <ErrorSharp fontSize="small" /> Please choose file with name "{retryUpload?.name}" in order to proceed
                                    or click cancel then edit the filename and click retry upload again.
                                </Typography>
                            )}
                            <Button
                                variant="contained"
                                size="small"
                                disabled={selectedFiles?.length < 1 || selectedFiles[0]?.name !== retryUpload?.name}
                                sx={{mt: 1, alignSelf: 'center', px: 2, py: 1}}
                                onClick={processAllFiles}>
                                Upload
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                sx={{mt: 1, mx: 1, alignSelf: 'center', px: 2, py: 1}}
                                onClick={() => setRetryUpload(null)}>
                                Cancel
                            </Button>
                        </Box>
                    ) : (
                        <Box>
                            {selectedFiles?.map((f: any) => (
                                <Typography key={f.name} variant="body" size="medium">
                                    {f.name}{' '}
                                    {finished.includes(f.name) ? (
                                        <CheckCircle />
                                    ) : (
                                        <GradientCircularProgress gradientId="gradient-add-files" />
                                    )}{' '}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Box>
            </Popup>

            <Popup isOpen={editMeta ? true : false}>
                <EditMetaForm file={editMeta} expId={experiment?.id} sId={sample?.id} refetch={onFileMetaEdited} setEdit={setEditMeta} />
            </Popup>
        </>
    );
};
